/**
 * /get-app — the one link to print (Round 40c, Sep 25 2026).
 *
 * A phone that opens this is sent straight to the right place: iPhone / iPad
 * to the iOS link, Android to the Android link — whatever Admin → App Store
 * Links has set and marked Published (a TestFlight or Play-testing link today,
 * the store listings once they are live). Desktops get both buttons.
 *
 * It lives OUTSIDE the /app beta gate on purpose: the page inside App.js was
 * unreachable for a stranger (AppRoot sent /get-app to the landing page, and
 * /app/get-app hit the private-beta redirect first), so a printed QR code
 * pointing here never worked. Every tap is counted (Admin → KPI).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { BRAND_ASSETS } from '../brandAssets';

const api = axios.create({ baseURL: `${API_BASE_URL}/api`, timeout: 10000 });

const trackStoreClick = (storeType, page) => {
  try {
    api.post('/app-store/track-click', { store_type: storeType, page, referrer: document.referrer }).catch(() => {});
  } catch (e) { /* never block the navigation */ }
};

const GetAppPage = () => {
  const [links, setLinks] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    api.get('/config/app-links').then((r) => setLinks(r.data || {})).catch(() => setLinks({}));
  }, []);

  useEffect(() => {
    if (!links) return;
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);
    if (isIOS && links.ios) {
      setRedirecting(true);
      trackStoreClick('apple_store', 'get-app-redirect');
      window.location.replace(links.ios);
    } else if (isAndroid && links.android) {
      setRedirecting(true);
      trackStoreClick('google_play', 'get-app-redirect');
      window.location.replace(links.android);
    }
  }, [links]);

  const label = (kind, href) => {
    if (!href) return null;
    if (kind === 'ios') return /testflight/i.test(href) ? 'Download for iPhone' : 'Download on the App Store';
    return /play\.google/i.test(href) ? 'Get it on Google Play' : 'Download for Android';
  };

  const btn = (kind, href, comingSoon) => (
    href ? (
      <a
        href={href}
        onClick={() => trackStoreClick(kind === 'ios' ? 'apple_store' : 'google_play', 'get-app')}
        className="block w-full rounded-xl py-4 text-center font-bold text-lg bg-emerald-500 text-white hover:bg-emerald-400 transition-colors"
        data-testid={`get-app-${kind}`}
      >
        {label(kind, href)}
      </a>
    ) : (
      <div className="block w-full rounded-xl py-4 text-center font-bold text-lg border border-emerald-500/40 text-gray-400" data-testid={`get-app-${kind}-soon`}>
        {comingSoon}
      </div>
    )
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#050b08] text-white" data-testid="get-app-page">
      <div className="max-w-md w-full text-center space-y-4">
        <img src={BRAND_ASSETS.logo} alt="GEMS" className="w-20 h-20 mx-auto rounded-2xl" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <h1 className="text-3xl font-bold">Get the GEMS app</h1>
        <p className="text-gray-400">Watch, chat, gift and play face-to-face with creators during their lives.</p>
        {!links ? (
          <p className="text-gray-400">Loading…</p>
        ) : redirecting ? (
          <p className="text-gray-400">Opening the download…</p>
        ) : (
          <>
            {btn('ios', links.ios, 'iPhone — App Store listing coming soon')}
            {btn('android', links.android, 'Android — Google Play listing coming soon')}
          </>
        )}
        {links && (links.ios || links.android) && (
          <p className="text-xs text-gray-500">
            {links.ios && /testflight/i.test(links.ios) ? 'iPhone: install the free TestFlight app first, then tap Start Testing. ' : ''}
            {links.android && /apps\/test/i.test(links.android) ? 'Android: tap Become a tester, then Download it on Google Play.' : ''}
          </p>
        )}
        <a href="/beta-access" className="block text-sm underline text-gray-400">Continue on the web</a>
      </div>
    </div>
  );
};

export default GetAppPage;
